import { NavigateFunction } from 'react-router-dom';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { PRIMARY_RED } from 'commons/styles/colors';
import { STOItem } from 'commons/types';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import {
  checkItem,
  setSTOCheckBasket,
  setSTOCheckBasketPhotoUrls,
} from 'redux-stores/reducers/stoCheckBasketReducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { STO_CHECK_ROUTES } from 'routes/constants';
import { swipeRxWmsApiStoCheckBasket } from 'services/apis/SwipeRxWmsApiStoCheckBasket';
import { swipeRxWmsApiStoCheckUpload } from 'services/apis/SwipeRxWmsApiStoCheckUpload';

export const getSTOCheckBasket = createAsyncThunk(
  'stoCheckBasket/getSTOCheckBasket',
  async (basket: number, { dispatch }) => {
    return defaultActionProcess(async () => {
      const response =
        await swipeRxWmsApiStoCheckBasket.getSTOCheckBasket(basket);
      if (response && response.data) {
        dispatch(setSTOCheckBasket(response.data));
      }
      return response.data;
    }, dispatch);
  },
);

export const getOnProgressSTOCheckBasket = createAsyncThunk(
  'stoCheckBasket/getOnProgressSTOCheckBasket',
  async (
    params: {
      stoNumber: string;
      basket: number;
    },
    { dispatch },
  ) => {
    return defaultActionProcess(async () => {
      const response =
        await swipeRxWmsApiStoCheckBasket.getOnProgressSTOCheckBasket(
          params.stoNumber,
          params.basket,
        );
      if (response && response.data) {
        dispatch(setSTOCheckBasket(response.data));
      }
      return response.data;
    }, dispatch);
  },
);

export const stoCheckUploadBasketPhoto = createAsyncThunk(
  'stoCheckBasket/stoCheckUploadBasketPhoto',
  async (
    params: {
      stoNumber: string;
      basket: number;
      imageSrc: string;
      onClose: () => void;
    },
    { dispatch },
  ) => {
    return defaultActionProcess(async () => {
      const SIZE_LIMIT = 1024 * 1024 * 2; // 2MB
      const blob = await fetch(params.imageSrc).then((r) => r.blob());

      if (blob.size > SIZE_LIMIT) {
        dispatch(
          snackbarSetData({
            open: true,
            message: 'File is too large',
            color: PRIMARY_RED,
          }),
        );
        return null;
      }

      const formData = new FormData();
      formData.append('file', blob, 'basket.jpg');

      const response = await swipeRxWmsApiStoCheckUpload.uploadPhoto(
        params.stoNumber,
        params.basket,
        formData,
      );
      if (response && response.data) {
        dispatch(setSTOCheckBasketPhotoUrls(response.data));
        params.onClose();
      }
      return response.data;
    }, dispatch);
  },
);

export const stoCheckDeleteBasketPhoto = createAsyncThunk(
  'stoCheckBasket/stoCheckDeleteBasketPhoto',
  async (
    params: {
      stoNumber: string;
      basket: number;
      photoLink: string;
    },
    { dispatch },
  ) => {
    return defaultActionProcess(async () => {
      const response = await swipeRxWmsApiStoCheckUpload.deleteBasketPhoto(
        params.stoNumber,
        params.basket,
        params.photoLink,
      );
      if (response && response.data) {
        dispatch(setSTOCheckBasketPhotoUrls(response.data));
      }
      return response.data;
    }, dispatch);
  },
);

export const stoCheckUpdateCheckedQty = createAsyncThunk(
  'stoCheckBasket/stoCheckUpdateCheckedQty',
  async (
    params: {
      stoNumber: string;
      basket: number;
      payload: STOItem;
    },
    { dispatch },
  ) => {
    const { stoNumber, basket, payload } = params;
    const { sku_code: skuCode, checking_metadata: checkingMetadata } = payload;
    return defaultActionProcess(async () => {
      const response = await swipeRxWmsApiStoCheckBasket.updateCheckedQty({
        basket,
        sku_code: skuCode,
        sto_number: stoNumber,
        total_qty_checked: checkingMetadata.total_qty_checked,
      });
      if (response && response.data) {
        dispatch(setSTOCheckBasket(response.data));
      }
      return response.data;
    }, dispatch);
  },
);

export const stoCheckUpdatePickedQty = createAsyncThunk(
  'stoCheckBasket/stoCheckUpdatePickedQty',
  async (
    params: {
      stoNumber: string;
      basket: number;
      payload: STOItem;
      onClose: () => void;
    },
    { dispatch },
  ) => {
    const { stoNumber, basket, payload } = params;
    const {
      sku_code: skuCode,
      checking_metadata: checkingMetadata,
      pick_actual: pickActual,
    } = payload;
    return defaultActionProcess(async () => {
      const response = await swipeRxWmsApiStoCheckBasket.updatePickedQty({
        basket,
        sku_code: skuCode,
        sto_number: stoNumber,
        pick_actual: pickActual,
        reason: checkingMetadata.reason,
      });
      if (response && response.data) {
        dispatch(setSTOCheckBasket(response.data));
        params.onClose();
      }
      return response.data;
    }, dispatch);
  },
);

export const stoCheckUpdateBoxNumberAndPallet = createAsyncThunk(
  'stoCheckBasket/stoCheckUpdateBoxNumberAndPallet',
  async (
    params: {
      stoNumber: string;
      basket: number;
      payload: STOItem;
    },
    { dispatch },
  ) => {
    const { stoNumber, basket, payload } = params;
    const { sku_code: skuCode, box_number: boxNumber, pallet } = payload;
    return defaultActionProcess(async () => {
      const response =
        await swipeRxWmsApiStoCheckBasket.updateBoxNumberAndPallet({
          basket,
          sku_code: skuCode,
          sto_number: stoNumber,
          box_number: boxNumber,
          pallet,
        });
      if (response && response.data) {
        dispatch(setSTOCheckBasket(response.data));
      }
      return response.data;
    }, dispatch);
  },
);

export const stoCheckItemAction = createAsyncThunk(
  'stoCheckBasket/stoCheckItemAction',
  async (
    params: {
      stoNumber: string;
      basket: number;
      skuCode: string;
      isChecked: boolean;
      itemIndex: number;
    },
    { dispatch },
  ) => {
    const { stoNumber, basket, skuCode, isChecked, itemIndex } = params;

    return defaultActionProcess(async () => {
      dispatch(
        checkItem({
          itemIndex,
          isChecked,
        }),
      );
      try {
        await swipeRxWmsApiStoCheckBasket.checkBasketInfo({
          sto_number: stoNumber,
          basket,
          sku_code: skuCode,
          is_checked: isChecked,
        });
      } catch (error: any) {
        dispatch(
          checkItem({
            itemIndex,
            isChecked: !isChecked,
          }),
        );
        throw new Error(error.message);
      }
    }, dispatch);
  },
);

export const stoCompleteChecking = createAsyncThunk(
  'stoCheckBasket/stoCompleteChecking',
  async (
    payload: { stoNumber: string; navigate: NavigateFunction },
    { dispatch },
  ) => {
    return defaultActionProcess(async () => {
      await swipeRxWmsApiStoCheckBasket.completeChecking(payload.stoNumber);
      dispatch(setSTOCheckBasket(null));
      payload.navigate(STO_CHECK_ROUTES.CHECK_BASKET);
    }, dispatch);
  },
);
